<script setup lang="ts">
import { motion } from 'motion-v'
import type { HTMLAttributes } from 'vue'

export interface MarqueeProps {
  speed?: number
  gap?: number
  direction?: 'left' | 'right'
  pauseOnHover?: boolean
  class?: HTMLAttributes['class']
  copyClass?: HTMLAttributes['class']
  trackClass?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<MarqueeProps>(), {
  speed: 50,
  gap: 48,
  direction: 'left',
  pauseOnHover: false,
  class: undefined,
  copyClass: undefined,
  trackClass: undefined
})

const containerRef = useTemplateRef('containerRef')
const trackRef = useTemplateRef('trackRef')
const seqRef = ref<HTMLElement | null>(null)

const seqWidth = ref<number>(0)
const copyCount = ref<number>(2)
const currentX = ref<number>(0)
const animationKey = ref<number>(0)
const isHovered = ref<boolean>(false)
const isReady = ref<boolean>(false)

const animateX = computed(() => {
  if (!isReady.value || seqWidth.value === 0) return 0
  if (isHovered.value && props.pauseOnHover) return currentX.value
  return props.direction === 'left' ? -seqWidth.value : seqWidth.value
})

const animationDuration = computed(() => {
  if (!isReady.value || seqWidth.value === 0 || props.speed === 0) return 0
  if (isHovered.value && props.pauseOnHover) return 0
  return seqWidth.value / Math.abs(props.speed)
})

const transitionConfig = computed(() => {
  if (!isReady.value) {
    return {
      duration: 0
    }
  }

  if (isHovered.value && props.pauseOnHover) {
    return {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const
    }
  }

  return {
    duration: animationDuration.value,
    ease: 'linear' as const,
    repeat: Infinity,
    repeatType: 'loop' as const
  }
})

const handleAnimationUpdate = (latest: Record<string, unknown>) => {
  if (typeof latest.x === 'number') {
    currentX.value = latest.x
  }
}

const handleMouseEnter = () => {
  if (props.pauseOnHover) {
    isHovered.value = true
  }
}

const handleMouseLeave = () => {
  if (props.pauseOnHover) {
    isHovered.value = false
    animationKey.value++
  }
}

const updateDimensions = async () => {
  await nextTick()
  const containerWidth = containerRef.value?.clientWidth ?? 0
  const sequenceWidth = seqRef.value?.getBoundingClientRect?.()?.width ?? 0

  if (sequenceWidth > 0) {
    seqWidth.value = Math.ceil(sequenceWidth)
    const copiesNeeded = Math.ceil(containerWidth / sequenceWidth) + 2
    copyCount.value = Math.max(2, copiesNeeded)

    // Mark as ready and trigger animation restart
    if (!isReady.value) {
      isReady.value = true
    }
    animationKey.value++
  }
}

let resizeObserver: ResizeObserver | null = null

const setupResizeObserver = () => {
  if (!window.ResizeObserver) {
    const handleResize = () => updateDimensions()
    window.addEventListener('resize', handleResize)
    updateDimensions()
    return () => window.removeEventListener('resize', handleResize)
  }

  resizeObserver = new ResizeObserver(updateDimensions)

  if (containerRef.value) {
    resizeObserver.observe(containerRef.value)
  }
  if (seqRef.value) {
    resizeObserver.observe(seqRef.value)
  }

  updateDimensions()

  return () => {
    resizeObserver?.disconnect()
    resizeObserver = null
  }
}

let cleanupResize: (() => void) | undefined

onMounted(async () => {
  await nextTick()
  setTimeout(() => {
    cleanupResize = setupResizeObserver()
  }, 10)

  // Fallback initialization
  setTimeout(() => {
    if (!isReady.value) {
      updateDimensions()
    }
  }, 100)
})

onUnmounted(() => {
  cleanupResize?.()
})
</script>

<template>
  <div
    ref="containerRef"
    :class="
      cn('relative overflow-hidden flex items-center w-full', props.class)
    "
    :style="{ '--marquee-gap': `${props.gap}px` }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <component
      :is="motion.div"
      :key="animationKey"
      ref="trackRef"
      :class="
        cn(
          'flex w-max select-none motion-reduce:transform-none',
          props.trackClass
        )
      "
      :initial="{ x: currentX }"
      :animate="{ x: animateX }"
      :transition="transitionConfig"
      @update="handleAnimationUpdate"
    >
      <div
        v-for="copyIndex in copyCount"
        :key="`copy-${copyIndex - 1}`"
        :ref="
          (el) => {
            if (copyIndex === 1) seqRef = el as HTMLElement
          }
        "
        :class="cn('flex items-center gap-3', props.copyClass)"
      >
        <slot />
      </div>
    </component>
  </div>
</template>
